import React from 'react';

interface MarkdownContentProps {
  /** The registry body to render. Parsed to React nodes — never injected as HTML. */
  markdown: string;
}

// Inline spans: [text](url) links, **bold**, *italic*. The link alternative is
// matched first so `[**label**](url)` degrades to plain text inside the link
// rather than nesting the two spans.
const INLINE_PATTERN = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;

/**
 * Only http(s), site-relative and in-page targets may become links; anything
 * else renders as its literal source text, so a registry body can never
 * produce a `javascript:` URL.
 */
function isSafeHref(href: string): boolean {
  return /^https?:\/\//.test(href) || href.startsWith('/') || href.startsWith('#');
}

/** Render one line/block of Markdown text to inline React nodes. */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(INLINE_PATTERN)) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const key = `${keyPrefix}-${nodes.length}`;
    if (match[1] !== undefined) {
      if (isSafeHref(match[2])) {
        nodes.push(
          <a key={key} href={match[2]} className="font-medium underline">
            {match[1]}
          </a>,
        );
      } else {
        nodes.push(match[0]);
      }
    } else if (match[3] !== undefined) {
      nodes.push(<strong key={key}>{match[3]}</strong>);
    } else if (match[4] !== undefined) {
      nodes.push(<em key={key}>{match[4]}</em>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

/**
 * Sibling-unique keys derived from item text — an exact-duplicate bullet gets
 * a `-2`/`-3` suffix. Array-index keys would churn if a body ever reorders.
 */
function itemEntries(items: readonly string[]): { key: string; text: string }[] {
  const counts = new Map<string, number>();
  return items.map((text) => {
    const seen = counts.get(text) ?? 0;
    counts.set(text, seen + 1);
    return { text, key: seen === 0 ? text : `${text}-${seen + 1}` };
  });
}

/**
 * The in-repo Markdown renderer for legal registry bodies. Supports what a
 * legal document uses — `#`/`##`/`###` headings, `-`/`1.` list items,
 * paragraphs, and the inline spans above — with zero dependencies. `#` maps to
 * `<h2>`: the page component owns the single `<h1>` (the document title), so a
 * body heading must not introduce a second one.
 */
export const MarkdownContent: React.FC<MarkdownContentProps> = ({ markdown }) => {
  const blocks: React.ReactNode[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | undefined;

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      const key = `p-${blocks.length}`;
      blocks.push(
        <p key={key}>{renderInline(paragraph.join(' '), key)}</p>,
      );
      paragraph = [];
    }
  };

  const flushList = () => {
    if (list) {
      const key = `${list.ordered ? 'ol' : 'ul'}-${blocks.length}`;
      const items = itemEntries(list.items).map((entry) => (
        <li key={entry.key}>{renderInline(entry.text, entry.key)}</li>
      ));
      blocks.push(
        list.ordered ? (
          <ol key={key} className="list-decimal list-inside space-y-1">
            {items}
          </ol>
        ) : (
          <ul key={key} className="list-disc list-inside space-y-1">
            {items}
          </ul>
        ),
      );
      list = undefined;
    }
  };

  for (const line of markdown.split('\n')) {
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    const unordered = /^\s*[-*]\s+(.*)$/.exec(line);
    const ordered = /^\s*\d+\.\s+(.*)$/.exec(line);

    if (heading !== null) {
      flushParagraph();
      flushList();
      const key = `h-${blocks.length}`;
      const content = renderInline(heading[2], key);
      blocks.push(
        heading[1].length === 1 ? (
          <h2 key={key} className="text-xl font-semibold">
            {content}
          </h2>
        ) : (
          <h3 key={key} className="text-lg font-semibold">
            {content}
          </h3>
        ),
      );
    } else if (unordered !== null || ordered !== null) {
      flushParagraph();
      const item = (unordered ?? ordered)![1];
      if (list === undefined || list.ordered !== (ordered !== null)) {
        flushList();
        list = { ordered: ordered !== null, items: [] };
      }
      list.items.push(item);
    } else if (line.trim() === '') {
      flushParagraph();
      flushList();
    } else {
      paragraph.push(line.trim());
    }
  }
  flushParagraph();
  flushList();

  return <div className="space-y-4 text-gray-800">{blocks}</div>;
};
